import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { AppRootWrapperBlueprint } from '@backstage/plugin-app-react';

// Backstage's new frontend system loads each PageBlueprint's content behind
// a Suspense boundary, so the freshly-routed page's <Content> DOM node may
// not exist for a few frames after the URL actually changes (route swap
// != paint). Poll via rAF for a bounded window instead of assuming it's
// there on the very next tick.
const MAX_POLL_FRAMES = 20;
const FADE_CLASS = 'rafay-page-fade';
const STYLE_ID = 'rafay-page-transition-styles';

// Content's stable class prefix (`BackstageContent`) comes from
// @backstage/core-components' Content.tsx, which names its JSS sheet
// `{ name: 'BackstageContent' }` — MUI v4 generates classes like
// `BackstageContent-root-<hash>`, so a partial-match selector is stable
// across builds instead of guessing the hashed suffix.
const CONTENT_SELECTOR = '[class*="BackstageContent-root"]';

const TRANSITION_CSS = `
@keyframes ${FADE_CLASS} {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
.${FADE_CLASS} {
  animation: ${FADE_CLASS} 280ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
@media (prefers-reduced-motion: reduce) {
  .${FADE_CLASS} { animation: none; }
}
`;

function ensureStylesInjected() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = TRANSITION_CSS;
  document.head.appendChild(style);
}

// Fades in the routed page's <Content> area on every navigation — scoped to
// that DOM node specifically (not the whole app root) so the sidebar and
// header never re-animate or lose state on route change, only the content
// below them.
function PageTransitionWrapper({ children }: { children: ReactNode }) {
  const location = useLocation();

  useEffect(() => {
    ensureStylesInjected();

    let frame = 0;
    let attempts = 0;

    const tryFade = () => {
      const el = document.querySelector<HTMLElement>(CONTENT_SELECTOR);
      if (el) {
        // Remove-then-reflow-then-add so the animation replays even if the
        // same DOM node persists across this navigation (e.g. moving
        // between two instances of the same route).
        el.classList.remove(FADE_CLASS);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        el.offsetWidth;
        el.classList.add(FADE_CLASS);
        return;
      }
      if (attempts++ < MAX_POLL_FRAMES) {
        frame = requestAnimationFrame(tryFade);
      }
    };

    frame = requestAnimationFrame(tryFade);
    return () => cancelAnimationFrame(frame);
  }, [location.pathname]);

  return <>{children}</>;
}

export const pageTransitionModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootWrapperBlueprint.make({
      name: 'page-transition',
      params: { component: PageTransitionWrapper },
    }),
  ],
});
